/* ============================================================
   FORMULA 21 — Neural "Waves" background (vanilla JS port)
   Ported from the claude.ai/design NeuralBgFlow.jsx prototype.
   Four layered passes on a fixed full-page canvas:
     1. SMOKE   — drifting volumetric blue light (additive blobs)
     2. HEX     — faint molecular honeycomb + breathing clusters
     3. RIBBONS — slow glowing cyan light-ribbons
     4. SPARKS  — slow rising glow particles
   Honors prefers-reduced-motion (renders one static frame, no loop).
   Single instance; safe to call repeatedly (Theme Editor reloads).
   ============================================================ */
(function () {
  function initF21Bg(canvas) {
    if (!canvas) return;
    var accent = canvas.getAttribute('data-accent') || '#24B0EC';

    // Tear down any previous instance bound to this canvas.
    if (canvas.__f21bgCleanup) { canvas.__f21bgCleanup(); }

    var ctx = canvas.getContext('2d');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var w = 0, h = 0, dpr = 1, raf = 0, t = 0;

    var hex = (accent || '#24B0EC').replace('#', '');
    var R = parseInt(hex.slice(0, 2), 16) || 36;
    var G = parseInt(hex.slice(2, 4), 16) || 176;
    var B = parseInt(hex.slice(4, 6), 16) || 236;
    var rgba = function (a) { return 'rgba(' + R + ',' + G + ',' + B + ',' + a + ')'; };
    var rgbaDeep = function (a) {
      return 'rgba(' + Math.round(R * 0.45) + ',' + Math.round(G * 0.55) + ',' + Math.round(B * 0.9) + ',' + a + ')';
    };

    var SIZE = 30;
    var smoke = [], ribbons = [], sparks = [], molecules = [];
    var hexCanvas = null;

    function hexPath(path, cx, cy, s) {
      for (var i = 0; i < 6; i++) {
        var ang = (Math.PI / 180) * (60 * i - 30);
        var x = cx + s * Math.cos(ang);
        var y = cy + s * Math.sin(ang);
        if (i === 0) path.moveTo(x, y); else path.lineTo(x, y);
      }
      path.closePath();
    }

    function buildHexLayer() {
      var hSpacing = Math.sqrt(3) * SIZE;
      var vSpacing = 1.5 * SIZE;
      hexCanvas = document.createElement('canvas');
      hexCanvas.width = Math.max(1, Math.floor(w * dpr));
      hexCanvas.height = Math.max(1, Math.floor(h * dpr));
      var hc = hexCanvas.getContext('2d');
      hc.setTransform(dpr, 0, 0, dpr, 0, 0);
      var grid = new Path2D();
      var rows = Math.ceil(h / vSpacing) + 2;
      var cols = Math.ceil(w / hSpacing) + 2;
      for (var r = -1; r < rows; r++) {
        for (var c = -1; c < cols; c++) {
          var cx = c * hSpacing + (r % 2 ? hSpacing / 2 : 0);
          var cy = r * vSpacing;
          hexPath(grid, cx, cy, SIZE - 1.5);
        }
      }
      hc.strokeStyle = rgba(0.05);
      hc.lineWidth = 1;
      hc.stroke(grid);
    }

    function newMoleculeCells() {
      var hSpacing = Math.sqrt(3) * SIZE;
      var vSpacing = 1.5 * SIZE;
      var rows = Math.max(4, Math.floor(h / vSpacing));
      var cols = Math.max(4, Math.floor(w / hSpacing));
      var r0 = 1 + Math.floor(Math.random() * (rows - 2));
      var c0 = 1 + Math.floor(Math.random() * (cols - 2));
      var target = 1 + Math.floor(Math.random() * 7);
      var key = function (r, c) { return r + ',' + c; };
      var neighbours = function (r, c) {
        return (r & 1) === 0
          ? [[r, c + 1], [r, c - 1], [r - 1, c], [r - 1, c - 1], [r + 1, c], [r + 1, c - 1]]
          : [[r, c + 1], [r, c - 1], [r - 1, c + 1], [r - 1, c], [r + 1, c + 1], [r + 1, c]];
      };
      var set = new Map([[key(r0, c0), [r0, c0]]]);
      var guard = 0;
      while (set.size < target && guard++ < 80) {
        var cellsArr = Array.from(set.values());
        var pick = cellsArr[Math.floor(Math.random() * cellsArr.length)];
        var open = neighbours(pick[0], pick[1]).filter(function (n) {
          return n[0] > 0 && n[1] > 0 && n[0] < rows - 1 && n[1] < cols - 1 && !set.has(key(n[0], n[1]));
        });
        if (!open.length) continue;
        var nb = open[Math.floor(Math.random() * open.length)];
        set.set(key(nb[0], nb[1]), [nb[0], nb[1]]);
      }
      return Array.from(set.values()).map(function (rc) {
        var r = rc[0], c = rc[1];
        return { cx: c * hSpacing + (r % 2 ? hSpacing / 2 : 0), cy: r * vSpacing };
      });
    }

    function spawnMolecule(stagger) {
      var fadeIn = 55 + Math.random() * 45;
      var hold = 70 + Math.random() * 110;
      var fadeOut = 60 + Math.random() * 55;
      var max = fadeIn + hold + fadeOut;
      return {
        cells: newMoleculeCells(),
        life: stagger ? Math.random() * max : 0,
        fadeIn: fadeIn, hold: hold, fadeOut: fadeOut, max: max,
        phase: Math.random() * Math.PI * 2,
        breathe: 0.012 + Math.random() * 0.01
      };
    }

    function build() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth || document.documentElement.clientWidth || 0;
      h = window.innerHeight || document.documentElement.clientHeight || 0;
      if (w <= 0 || h <= 0) return false;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      buildHexLayer();

      smoke = [];
      for (var i = 0; i < 7; i++) {
        smoke.push({
          x: Math.random() * w, y: Math.random() * h,
          rad: (0.28 + Math.random() * 0.34) * Math.max(w, h),
          vx: (Math.random() - 0.5) * 0.10, vy: (Math.random() - 0.5) * 0.07,
          ph: Math.random() * Math.PI * 2, deep: i % 3 === 0,
          base: 0.05 + Math.random() * 0.05
        });
      }

      ribbons = [];
      for (var j = 0; j < 4; j++) {
        ribbons.push({
          baseY: h * (0.18 + j * 0.21), amp: 55 + Math.random() * 70,
          freq: 0.0022 + Math.random() * 0.0020,
          speed: (0.007 + Math.random() * 0.006) * (j % 2 ? -1 : 1),
          phase: Math.random() * Math.PI * 2, drift: (Math.random() - 0.5) * 0.08,
          thick: 1.0 + Math.random() * 0.5
        });
      }

      sparks = [];
      for (var k = 0; k < 46; k++) {
        sparks.push({
          x: Math.random() * w, y: Math.random() * h,
          r: 0.6 + Math.random() * 1.6, vy: -(0.08 + Math.random() * 0.22),
          sway: 0.2 + Math.random() * 0.5, ph: Math.random() * Math.PI * 2,
          tw: 0.6 + Math.random() * 1.4
        });
      }

      molecules = [];
      for (var m = 0; m < 6; m++) molecules.push(spawnMolecule(true));
      return true;
    }

    function paintBg() {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#04060b';
      ctx.fillRect(0, 0, w, h);
    }

    function drawSmoke() {
      ctx.globalCompositeOperation = 'lighter';
      for (var i = 0; i < smoke.length; i++) {
        var s = smoke[i];
        if (!reduce) { s.x += s.vx; s.y += s.vy; }
        if (s.x < -s.rad) s.x = w + s.rad; else if (s.x > w + s.rad) s.x = -s.rad;
        if (s.y < -s.rad) s.y = h + s.rad; else if (s.y > h + s.rad) s.y = -s.rad;
        var pulse = 0.7 + 0.3 * Math.sin(t * 0.006 + s.ph);
        var a = s.base * pulse;
        var cx = s.x + Math.sin(t * 0.004 + s.ph) * 30;
        var cy = s.y + Math.cos(t * 0.005 + s.ph) * 24;
        var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, s.rad);
        var col = s.deep ? rgbaDeep : rgba;
        g.addColorStop(0, col(a));
        g.addColorStop(0.45, col(a * 0.4));
        g.addColorStop(1, 'rgba(4,6,11,0)');
        ctx.fillStyle = g;
        ctx.fillRect(cx - s.rad, cy - s.rad, s.rad * 2, s.rad * 2);
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    function drawHex() {
      if (hexCanvas && hexCanvas.width > 0 && hexCanvas.height > 0) {
        ctx.globalAlpha = 0.9;
        ctx.drawImage(hexCanvas, 0, 0, w, h);
        ctx.globalAlpha = 1;
      }
      ctx.globalCompositeOperation = 'lighter';
      for (var i = 0; i < molecules.length; i++) {
        var m = molecules[i];
        if (!reduce) m.life += 1;
        if (m.life >= m.max) { molecules[i] = spawnMolecule(false); m = molecules[i]; }
        var env;
        if (m.life < m.fadeIn) env = m.life / m.fadeIn;
        else if (m.life < m.fadeIn + m.hold) env = 1;
        else env = Math.max(0, 1 - (m.life - m.fadeIn - m.hold) / m.fadeOut);
        var breathe = reduce ? 1 : 0.78 + 0.22 * Math.sin(t * m.breathe + m.phase);
        var glow = env * breathe;
        if (glow < 0.01) continue;
        ctx.shadowColor = rgba(0.9);
        ctx.shadowBlur = 8 * glow;
        ctx.strokeStyle = rgba(0.12 + 0.22 * glow);
        ctx.lineWidth = 1.2;
        var cell, p, c;
        for (c = 0; c < m.cells.length; c++) {
          cell = m.cells[c]; p = new Path2D();
          hexPath(p, cell.cx, cell.cy, SIZE - 1.5); ctx.stroke(p);
        }
        ctx.shadowBlur = 6 * glow;
        ctx.fillStyle = rgba(0.4 + 0.4 * glow);
        for (c = 0; c < m.cells.length; c++) {
          cell = m.cells[c];
          ctx.beginPath(); ctx.arc(cell.cx, cell.cy, 1.7, 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = 'source-over';
    }

    function drawRibbons() {
      ctx.globalCompositeOperation = 'lighter';
      for (var i = 0; i < ribbons.length; i++) {
        var s = ribbons[i];
        if (!reduce) {
          s.baseY += s.drift;
          if (s.baseY < -60) s.baseY = h + 60; else if (s.baseY > h + 60) s.baseY = -60;
        }
        var pts = [];
        for (var x = -40; x <= w + 40; x += 10) {
          var y = s.baseY +
            s.amp * Math.sin(x * s.freq + t * s.speed + s.phase) +
            s.amp * 0.34 * Math.sin(x * s.freq * 2.2 + t * s.speed * 1.7 + s.phase);
          pts.push([x, y]);
        }
        ctx.beginPath();
        pts.forEach(function (pt, idx) { idx ? ctx.lineTo(pt[0], pt[1]) : ctx.moveTo(pt[0], pt[1]); });
        ctx.shadowColor = rgba(0.55); ctx.shadowBlur = 22;
        ctx.strokeStyle = rgba(0.06); ctx.lineWidth = s.thick * 4; ctx.stroke();
        ctx.beginPath();
        pts.forEach(function (pt, idx) { idx ? ctx.lineTo(pt[0], pt[1]) : ctx.moveTo(pt[0], pt[1]); });
        ctx.shadowBlur = 12; ctx.strokeStyle = rgba(0.18); ctx.lineWidth = s.thick; ctx.stroke();
      }
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = 'source-over';
    }

    function drawSparks() {
      ctx.globalCompositeOperation = 'lighter';
      ctx.shadowColor = rgba(0.9);
      for (var i = 0; i < sparks.length; i++) {
        var p = sparks[i];
        if (!reduce) {
          p.y += p.vy;
          p.x += Math.sin(t * 0.01 + p.ph) * p.sway * 0.15;
          if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; }
        }
        var tw = 0.5 + 0.5 * Math.sin(t * 0.03 * p.tw + p.ph);
        ctx.shadowBlur = 6 * tw;
        ctx.fillStyle = rgba(0.3 + 0.5 * tw);
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = 'source-over';
    }

    function frame() {
      if (w <= 0 || h <= 0) return;
      paintBg(); drawSmoke(); drawHex(); drawRibbons(); drawSparks();
      t += 1;
      if (!reduce) raf = requestAnimationFrame(frame);
    }

    var started = false;
    function startWhenReady(attempts) {
      if (build()) { started = true; frame(); return; }
      if (attempts > 0) requestAnimationFrame(function () { startWhenReady(attempts - 1); });
    }
    startWhenReady(20);

    var resizeTimer = 0;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (!started) { startWhenReady(20); return; }
        if (build() && reduce) frame();
      }, 150);
    }
    window.addEventListener('resize', onResize);

    canvas.__f21bgCleanup = function () {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      canvas.__f21bgCleanup = null;
    };
  }

  function boot() {
    var canvas = document.querySelector('.f21-neural-canvas');
    if (canvas) {
      document.body.classList.add('f21-has-bg');
      initF21Bg(canvas);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme Editor: re-init when the background section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.f21-neural-canvas')) {
      document.body.classList.add('f21-has-bg');
      boot();
    }
  });
})();
